import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
  FullScreenOverlayBody,
  FullScreenOverlayContent,
  FullScreenOverlayHeader
} from '@bibliocommons/base-full-screen-overlay';
import { libraryShape, paginationShape, subscriptionShape } from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';

import ILSOfflineMessage from 'app/components/bibs/shared/ILSOfflineMessage';
import { TableStencil } from 'app/components/shared/Table';
import StackMapDisplay from 'app/components/bibs/shared/StackMap/StackMapDisplay';
import UniversalBibBriefContainer from 'app/components/bibs/shared/UniversalBibBrief/UniversalBibBriefContainer';
import Tabs from 'app/components/shared/Tabs';
import { METADATA_CATEGORY_TYPE, UI_COMPONENT_TYPE, UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import bibItemShape from 'app/shapes/bibItemShape';
import stackMapStateShape from 'app/shapes/stackMapStateShape';
import Subscriptions from './Subscriptions';
import ItemAvailabilityTable from './ItemAvailabilityTable';

import './AvailabilityDetails.scss';

const AVAILABILITY_TABS = {
  CIRCULATION: 'circulation',
  SUBSCRIPTIONS: 'subscription'
};

export class AvailabilityDetails extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  state = {
    isStackMapOpen: false
  };

  handleStackMapOpen = (domain, data) => {
    const { fetchStackMap } = this.props;

    this.setState({ isStackMapOpen: true });

    return fetchStackMap(domain, data);
  };

  trackRequestFormClick({ itemId, linkText }) {
    const {
      currentView,
      metadataId,
      pagination,
      sortBys,
      trackRequestExternalPlace,
      itemPosition,
      itemPositionGrouped,
      totalCount
    } = this.props;
    const isFromSearchPage = window?.location?.pathname === '/v2/search';

    trackRequestExternalPlace({
      bibId: metadataId,
      metadata: {
        metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_RESOURCE_ITEM_ID,
        metadata_value: itemId
      },
      ui: {
        ui_component_label: linkText,
        ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
        ...(isFromSearchPage && {
          ui_content_position: itemPosition,
          ui_content_position_grouped: itemPositionGrouped,
          ui_content_position_grouped_count: totalCount,
          ui_container_type: UI_CONTAINER_TYPE.ITEM,
          ui_content_layout: currentView,
          ui_content_page: pagination.get('page'),
          ui_content_sort: sortBys.find(sortBy => sortBy.get('applied'))?.get('id'),
          ui_content_total_count: pagination.get('count')
        })
      }
    });
  }

  renderCirculation() {
    const {
      bibItems,
      currentLibrary,
      probes,
      itemPosition,
      itemPositionGrouped,
      stackMapState,
      totalCount
    } = this.props;

    return (
      <ItemAvailabilityTable
        bibItems={bibItems}
        currentLibrary={currentLibrary}
        handleStackMapOpen={this.handleStackMapOpen}
        probes={probes}
        trackRequestFormClick={this.trackRequestFormClick}
        itemPosition={itemPosition}
        itemPositionGrouped={itemPositionGrouped}
        stackMapState={stackMapState}
        totalCount={totalCount}
      />
    );
  }

  renderSubscriptions() {
    const { subscriptions } = this.props;
    return <Subscriptions subscriptions={subscriptions} />;
  }

  renderTabs() {
    const { intl } = this.props;

    const newTabOptions = [
      {
        id: AVAILABILITY_TABS.SUBSCRIPTIONS,
        label: intl.formatMessage({ id: `availability_modal_tab_${AVAILABILITY_TABS.SUBSCRIPTIONS}` }),
        renderPanelBody: this.renderSubscriptions
      },
      {
        id: AVAILABILITY_TABS.CIRCULATION,
        label: intl.formatMessage({ id: `availability_modal_tab_${AVAILABILITY_TABS.CIRCULATION}` }),
        renderPanelBody: this.renderCirculation
      }
    ];

    return <Tabs ariaLabel={intl.formatMessage({ id: 'availability_details' })} options={newTabOptions} />;
  }

  renderMainContent() {
    const { stackMapState } = this.props;
    const { isStackMapOpen } = this.state;

    if (this.props.isFetching) {
      return <TableStencil />;
    }
    if (isStackMapOpen) {
      return <StackMapDisplay stackMapState={stackMapState} />;
    }
    if (this.props.bibItems.size > 0 && this.props.subscriptions.size > 0) return this.renderTabs();
    if (this.props.bibItems.size > 0) return this.renderCirculation();
    if (this.props.subscriptions.size > 0) return this.renderSubscriptions();
    return null;
  }

  render() {
    const { availabilityError, isFetching, metadataId } = this.props;

    const title = this.state.stackMapOpen ? (
      <FormattedMessage id="find_item" />
    ) : (
      <FormattedMessage id="availability_details" />
    );

    return (
      <FullScreenOverlayBody className="cp-availability-details">
        <FullScreenOverlayHeader title={title} />
        <FullScreenOverlayContent>
          <UniversalBibBriefContainer metadataId={metadataId} />
          {!isFetching && <ILSOfflineMessage availabilityError={availabilityError} />}
          {this.renderMainContent()}
        </FullScreenOverlayContent>
      </FullScreenOverlayBody>
    );
  }
}

AvailabilityDetails.propTypes = {
  availabilityError: PropTypes.string,
  bibItems: ImmutablePropTypes.listOf(bibItemShape).isRequired,
  currentLibrary: libraryShape.isRequired,
  currentView: PropTypes.string,
  fetchStackMap: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  metadataId: PropTypes.string.isRequired,
  pagination: paginationShape,
  probes: ImmutablePropTypes.list.isRequired,
  sortBys: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      id: PropTypes.string.isRequired,
      applied: PropTypes.bool
    })
  ),
  stackMapState: stackMapStateShape.isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired,
  trackRequestExternalPlace: PropTypes.func.isRequired
};

export default injectIntl(AvailabilityDetails);
